import React, { useRef } from 'react';

import { Box } from '@mui/material';
import { DateField, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/src/style.css';

import dayjs from '@/components/NewUI/utils/dayjs';

import { SCOUT_BLUE } from '../../../../ThemeContext/constants';
import { sharedClasses } from '../../CustomUIElements/sharedClasses';

import type { Dayjs } from 'dayjs';
import type { Dispatch } from 'react';
import type { DateRange as RdpDateRange } from 'react-day-picker';

interface DateRange {
  from: Date | null;
  to: Date | null;
}
export interface TempDateRange {
  from: Dayjs | null;
  to: Dayjs | null;
}

export type DateRangeAction =
  | {
      type: 'SET_DATE_RANGE_TEMP_FILTER';
      payload: DateRange;
    }
  | {
      type: 'SET_DATE_FIELD_RANGE';
      payload: TempDateRange;
    };

const MIN_DATE = dayjs('01/01/1990', 'DD/MM/YYYY');

const dateFieldClass = {
  padding: '8px 0px 8px 0px',
  textAlign: 'center',
  fontSize: '14px',
  fontFamily: 'Source Sans Pro'
};

const datefieldsContainerClass = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  columnGap: '8px',
  padding: '12px 2px 6px 2px',
  fontSize: '14px',
  fontWeight: 600,
  fontFamily: 'Source Sans Pro',
  '& span': {
    color: SCOUT_BLUE
  }
};

const DateRangeFilter = ({
  dispatch,
  dateRange,
  dateRangeTemp,
  label
}: {
  dispatch: Dispatch<DateRangeAction>;
  dateRange: DateRange;
  dateRangeTemp: TempDateRange;
  label: string;
}) => {
  const dateRangeInputsRefs = useRef<{
    from: HTMLInputElement | null;
    to: HTMLInputElement | null;
  }>({ from: null, to: null });

  // Changes the date range when a day is clicked on the calendar
  const handleRangeSelect = (range: RdpDateRange | undefined) => {
    const newRange = { from: range?.from ?? null, to: range?.to ?? null };
    // Add the new range to the temp filter
    dispatch({ type: 'SET_DATE_RANGE_TEMP_FILTER', payload: newRange });
    // Update the text input date fields
    dispatch({
      type: 'SET_DATE_FIELD_RANGE',
      payload: { from: dayjs(newRange.from), to: dayjs(newRange.to) }
    });
  };

  // Swaps the to/from dates and field focus if the from date is after the to date
  const handleDateOrder = (range: { from: Date | null; to: Date | null }) => {
    if (dayjs(range.from).isAfter(dayjs(range.to))) {
      dispatch({
        type: 'SET_DATE_FIELD_RANGE',
        payload: { from: dayjs(range.to), to: dayjs(range.from) }
      });
      if (dateRangeInputsRefs.current.from === document.activeElement) {
        dateRangeInputsRefs.current.to?.focus();
      } else {
        dateRangeInputsRefs.current.from?.focus();
      }
      return { from: range.to, to: range.from };
    }
    return range;
  };

  // Changes the date range when the date field is edited
  const handleDateFieldChange = (date: Dayjs | null, type: 'from' | 'to') => {
    if (type === 'from') {
      dispatch({
        type: 'SET_DATE_FIELD_RANGE',
        payload: { from: date, to: dateRangeTemp.to }
      });
      if (date?.isValid() && date.isAfter(MIN_DATE)) {
        dispatch({
          type: 'SET_DATE_RANGE_TEMP_FILTER',
          payload: handleDateOrder({ from: date.toDate(), to: dateRangeTemp.to?.toDate() ?? null })
        });
      }
    } else {
      dispatch({
        type: 'SET_DATE_FIELD_RANGE',
        payload: { from: dateRangeTemp.from, to: date }
      });
      if (date?.isValid() && date.isAfter(MIN_DATE)) {
        dispatch({
          type: 'SET_DATE_RANGE_TEMP_FILTER',
          payload: handleDateOrder({
            from: dateRangeTemp.from?.toDate() ?? null,
            to: date.toDate()
          })
        });
      }
    }
  };
  return (
    <Box
      id={`${label}date-range-filter`}
      sx={{
        ...sharedClasses.datePickerCalendar,
        ...(dateRange.from && dateRange.to === null ? sharedClasses.onlyStartDate : {})
      }}
    >
      <DayPicker
        mode="range"
        onSelect={handleRangeSelect}
        selected={{ from: dateRange.from ?? undefined, to: dateRange.to ?? undefined }}
        modifiers={{
          first: { dayOfWeek: [0] },
          last: { dayOfWeek: [6] }
        }}
        modifiersStyles={{
          first: {
            borderTopLeftRadius: '50%',
            borderBottomLeftRadius: '50%'
          },
          last: {
            borderTopRightRadius: '50%',
            borderBottomRightRadius: '50%'
          }
        }}
      />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={datefieldsContainerClass}>
          <DateField
            format="DD/MM/YYYY"
            inputProps={{ sx: dateFieldClass }}
            minDate={MIN_DATE}
            value={dateRangeTemp.from}
            inputRef={(ref) => {
              dateRangeInputsRefs.current.from = ref;
            }}
            onChange={(value) =>
              handleDateFieldChange(value ? dayjs(value.toDate()) : null, 'from')
            }
          />
          <span>to</span>
          <DateField
            format="DD/MM/YYYY"
            inputProps={{ sx: dateFieldClass }}
            minDate={MIN_DATE}
            onChange={(value) => handleDateFieldChange(value ? dayjs(value.toDate()) : null, 'to')}
            value={dateRangeTemp.to}
            inputRef={(ref) => {
              dateRangeInputsRefs.current.to = ref;
            }}
          />
        </Box>
      </LocalizationProvider>
    </Box>
  );
};

export default DateRangeFilter;
